import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractUsPhoneDigits, formatUsPhoneInput } from '../../utils/usPhone';
import UsAddressAutocomplete from '../ui/UsAddressAutocomplete';
import ProspectLevelStars from '../ui/ProspectLevelStars';
import { inputClassWithError } from '../ui/formStyles';

const VALIDATED_FIELDS = ['name', 'email', 'phone'];

export function emptyProspectForm() {
  return {
    name: '',
    email: '',
    phone: '',
    address: '',
    apartment: '',
    notes: '',
    is_employed: null,
    is_married: null,
    housing_type: null,
    prospection_level: 0,
    prospect_type: 'client',
  };
}

function emptyTouched() {
  return { name: false, email: false, phone: false };
}

function emptyErrors() {
  return { name: '', email: '', phone: '' };
}

function validateField(key, form, p, t) {
  switch (key) {
    case 'name':
      if (!form.name.trim()) return p.nameRequired || 'Name is required';
      return '';
    case 'email': {
      const email = form.email.trim();
      if (email && !email.includes('@')) {
        return p.emailInvalid || 'Enter a valid email address';
      }
      return '';
    }
    case 'phone': {
      const digits = extractUsPhoneDigits(form.phone);
      if (digits && digits.length !== 10) {
        return p.phoneInvalid || t.referral?.phoneInvalid || 'Enter a valid 10-digit US phone number';
      }
      return '';
    }
    default:
      return '';
  }
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-xs text-red-600 " role="alert">
      {message}
    </p>
  );
}

export function segmentButtonClass(isActive) {
  return [
    'min-h-[44px] min-w-[3.25rem] flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40',
    isActive
      ? 'bg-gradient-to-b from-orange-500 to-orange-600 text-white shadow-soft'
      : 'text-secondary-600 hover:bg-secondary-100/90  ',
  ].join(' ');
}

export function YesNoField({ label, value, onChange, yesLabel, noLabel }) {
  return (
    <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_9.5rem] sm:gap-4">
      <span className="text-sm font-medium text-secondary-800 ">{label}</span>
      <div
        className="inline-flex w-full rounded-xl border border-secondary-200/80 bg-white/80 p-1 shadow-soft  "
        role="group"
        aria-label={label}
      >
        <button
          type="button"
          className={segmentButtonClass(value === true)}
          aria-pressed={value === true}
          onClick={() => onChange(value === true ? null : true)}
        >
          {yesLabel}
        </button>
        <button
          type="button"
          className={segmentButtonClass(value === false)}
          aria-pressed={value === false}
          onClick={() => onChange(value === false ? null : false)}
        >
          {noLabel}
        </button>
      </div>
    </div>
  );
}

export function HousingField({ label, value, onChange, ownLabel, rentLabel }) {
  return (
    <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_9.5rem] sm:gap-4">
      <span className="text-sm font-medium text-secondary-800 ">{label}</span>
      <div
        className="inline-flex w-full rounded-xl border border-secondary-200/80 bg-white/80 p-1 shadow-soft  "
        role="group"
        aria-label={label}
      >
        <button
          type="button"
          className={segmentButtonClass(value === 'OWN')}
          aria-pressed={value === 'OWN'}
          onClick={() => onChange(value === 'OWN' ? null : 'OWN')}
        >
          {ownLabel}
        </button>
        <button
          type="button"
          className={segmentButtonClass(value === 'RENT')}
          aria-pressed={value === 'RENT'}
          onClick={() => onChange(value === 'RENT' ? null : 'RENT')}
        >
          {rentLabel}
        </button>
      </div>
    </div>
  );
}

function CreateProspectWizard({ isOpen, onClose, isSubmitting, onSubmit }) {
  const { t } = useLanguage();
  const p = t.vendor?.prospects || {};
  const [form, setForm] = useState(emptyProspectForm);
  const [touched, setTouched] = useState(emptyTouched);
  const [errors, setErrors] = useState(emptyErrors);

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyProspectForm());
      setTouched(emptyTouched());
      setErrors(emptyErrors());
    }
  }, [isOpen]);

  function fieldErrorClass(field) {
    return !!(touched[field] && errors[field]);
  }

  const inputClass = (field) => inputClassWithError(fieldErrorClass(field));

  function validateAndSetField(key, nextForm) {
    const message = validateField(key, nextForm, p, t);
    setErrors((prev) => ({ ...prev, [key]: message }));
    return message;
  }

  function handleBlur(key) {
    setTouched((prev) => ({ ...prev, [key]: true }));
    validateAndSetField(key, form);
  }

  function updateField(key, value) {
    setForm((prev) => {
      const nextForm = { ...prev, [key]: value };
      if (touched[key]) {
        validateAndSetField(key, nextForm);
      }
      return nextForm;
    });
  }

  function touchAllValidated() {
    setTouched({ name: true, email: true, phone: true });
    const nextErrors = VALIDATED_FIELDS.reduce((acc, key) => {
      acc[key] = validateField(key, form, p, t);
      return acc;
    }, {});
    setErrors(nextErrors);
    return VALIDATED_FIELDS.every((key) => !nextErrors[key]);
  }

  const canSubmit = VALIDATED_FIELDS.every((key) => !validateField(key, form, p, t));

  async function finish(e) {
    e.preventDefault();
    if (!touchAllValidated()) return;
    await onSubmit(form);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={p.newProspect || 'New prospect'}
      size="prospect"
    >
      <form onSubmit={(e) => void finish(e)} className="space-y-4 lg:space-y-3">
        <p className="text-sm text-secondary-600 ">
          {p.createModalDesc ||
            'Save the prospect to your pipeline. Contact fields are optional and can be edited later.'}
        </p>

        <div className="space-y-1.5">
          <span className="text-xs font-medium text-secondary-700 ">
            {p.typeLabel || 'Prospect type'}
          </span>
          <div
            className="inline-flex w-full rounded-xl border border-secondary-200/80 bg-white/80 p-1 shadow-soft  "
            role="group"
            aria-label={p.typeLabel || 'Prospect type'}
          >
            <button
              type="button"
              className={segmentButtonClass(form.prospect_type === 'client')}
              aria-pressed={form.prospect_type === 'client'}
              onClick={() => updateField('prospect_type', 'client')}
            >
              {p.typeClient || 'Client'}
            </button>
            <button
              type="button"
              className={segmentButtonClass(form.prospect_type === 'partner')}
              aria-pressed={form.prospect_type === 'partner'}
              onClick={() => updateField('prospect_type', 'partner')}
            >
              {p.typePartner || 'Partner'}
            </button>
          </div>
        </div>

        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-3 lg:space-y-0">
          <div className="space-y-4 lg:space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-secondary-700 ">
                {p.nameLabel || 'Full name or business *'}
              </span>
              <input
                className={inputClass('name')}
                autoFocus
                placeholder={p.namePlaceholder || 'e.g. Jane Smith or Acme Water Co.'}
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                aria-invalid={touched.name && Boolean(errors.name)}
                aria-describedby={errors.name ? 'prospect-name-error' : undefined}
              />
              <FieldError id="prospect-name-error" message={touched.name ? errors.name : ''} />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-secondary-700 ">
                  {p.emailLabel || 'Email'}
                </span>
                <input
                  className={inputClass('email')}
                  type="email"
                  autoComplete="email"
                  placeholder={p.emailPlaceholder || 'name@company.com'}
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  aria-invalid={touched.email && Boolean(errors.email)}
                  aria-describedby={errors.email ? 'prospect-email-error' : undefined}
                />
                <FieldError id="prospect-email-error" message={touched.email ? errors.email : ''} />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-secondary-700 ">
                  {p.phoneLabel || 'Phone'}
                </span>
                <input
                  className={inputClass('phone')}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="(555) 555-0123"
                  maxLength={14}
                  value={form.phone}
                  onChange={(e) => updateField('phone', formatUsPhoneInput(e.target.value))}
                  onBlur={() => handleBlur('phone')}
                  aria-invalid={touched.phone && Boolean(errors.phone)}
                  aria-describedby={errors.phone ? 'prospect-phone-error' : undefined}
                />
                <FieldError id="prospect-phone-error" message={touched.phone ? errors.phone : ''} />
              </label>
            </div>

            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_8rem]">
              <label className="block">
                <span className="text-xs font-medium text-secondary-700 ">
                  {p.addressLabel || 'Address'}
                </span>
                <UsAddressAutocomplete
                  className={inputClass('address')}
                  placeholder={p.addressPlaceholder || '123 Main St, City, ST 12345 (optional)'}
                  searchingLabel={p.addressSearching || 'Searching addresses…'}
                  value={form.address}
                  onChange={(address) => setForm((f) => ({ ...f, address }))}
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-secondary-700 ">
                  {p.apartmentLabel || 'Apartment'}
                </span>
                <input
                  className={inputClass('apartment')}
                  placeholder={p.apartmentPlaceholder || 'Apt, suite, unit… (optional)'}
                  value={form.apartment}
                  onChange={(e) => setForm((f) => ({ ...f, apartment: e.target.value }))}
                />
              </label>
            </div>
          </div>

          <div className="space-y-4 lg:space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-secondary-700 ">
                {p.notesLabel || 'Notes'}
              </span>
              <textarea
                className={`${inputClass('notes')} min-h-[88px] resize-y lg:min-h-[72px]`}
                placeholder={p.notesPlaceholder || 'Context for your team — next step, source, etc.'}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>

            {form.prospect_type !== 'partner' && (
              <>
                <fieldset className="rounded-xl border border-secondary-200/70 bg-secondary-50/50 p-4 lg:p-3  ">
                  <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-secondary-600 ">
                    {p.creationProfileTitle || 'Prospect creation'}
                  </legend>
              <p className="mt-1 px-1 text-xs text-secondary-500 ">
                {p.creationProfileOptional || 'Optional — leave unanswered if unknown.'}
              </p>
              <div className="mt-3 space-y-3 lg:mt-2 lg:space-y-2.5">
                <YesNoField
                  label={p.worksLabel || 'Works?'}
                  value={form.is_employed}
                  onChange={(next) => updateField('is_employed', next)}
                  yesLabel={t.common?.yes || 'Yes'}
                  noLabel={t.common?.no || 'No'}
                />
                <YesNoField
                  label={p.marriedLabel || 'Married?'}
                  value={form.is_married}
                  onChange={(next) => updateField('is_married', next)}
                  yesLabel={t.common?.yes || 'Yes'}
                  noLabel={t.common?.no || 'No'}
                />
                <HousingField
                  label={p.housingLabel || 'Own home or rent?'}
                  value={form.housing_type}
                  onChange={(next) => setForm((f) => ({ ...f, housing_type: next }))}
                  ownLabel={p.ownsHomeLabel || 'Own home'}
                  rentLabel={p.rentsHomeLabel || 'Rent'}
                />
              </div>
            </fieldset>

            <label className="block">
              <span className="text-xs font-medium text-secondary-700 ">
                {p.prospectionLevelLabel || 'Prospection level'}
              </span>
              <p className="mt-0.5 text-xs text-secondary-500 ">
                {p.prospectionLevelHint || 'Optional — rate the quality of this prospect from 1 to 5 stars.'}
              </p>
              <div className="mt-1.5 flex justify-center py-1 lg:justify-start lg:py-0">
                <ProspectLevelStars
                  value={form.prospection_level}
                  onChange={(level) => setForm((f) => ({ ...f, prospection_level: level }))}
                  size="lg"
                  centered
                  ariaLabel={p.prospectionLevelLabel || 'Prospection level'}
                />
              </div>
            </label>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-secondary-200/60 pt-4 max-lg:[&_button]:w-full lg:flex-row lg:justify-end lg:gap-3  lg:[&_button]:w-auto">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t.common?.cancel || 'Cancel'}
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting} loading={isSubmitting}>
            {isSubmitting ? (p.saving || 'Saving…') : (p.create || 'Create prospect')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateProspectWizard;
